import React, { Fragment, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Form, FormGroup, Label, Input, Table, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetStoresQuery, useGetSuppliersQuery, useCreateReturnSupplierMutation, useUpdateReturnSupplierMutation, useGetReturnSupplierByIdQuery } from '../../../../store/services/inventoryApi';
import { Trash } from 'react-feather';
import ProductSearch from '../../../Common/ProductSearch';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
    storeId: yup.string().required('Store is required'),
    supplierId: yup.string().required('Supplier is required'),
    notes: yup.string(),
    items: yup.array().of(
        yup.object().shape({
            productVariantId: yup.string().required(),
            productName: yup.string(),
            sku: yup.string(),
            quantity: yup.number().required().min(1, 'Qty must be at least 1').typeError('Qty must be a number'),
            reason: yup.string().required('Reason is required'),
        })
    ).min(1, 'At least one item is required')
});

const RETURN_REASONS = [
    "Expired Stock",
    "Damaged Items",
    "Wrong Supply",
    "Excess Stock",
    "Other"
];

const ReturnToSupplierForm = () => {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const { data: storesData } = useGetStoresQuery();
    const { data: suppliersData } = useGetSuppliersQuery();
    const [createReturn, { isLoading: isCreating }] = useCreateReturnSupplierMutation();
    const [updateReturn, { isLoading: isUpdating }] = useUpdateReturnSupplierMutation();
    const { data: returnResponse, isLoading: isLoadingData } = useGetReturnSupplierByIdQuery(id, { skip: !isEdit });
    const returnData = returnResponse?.data || returnResponse;

    const { control, register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            storeId: '',
            supplierId: '',
            notes: '',
            items: []
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items"
    });

    const items = watch('items');
    const storeId = watch('storeId');

    useEffect(() => {
        if (returnData && isEdit) {
            reset({
                storeId: returnData.storeId?._id || returnData.storeId,
                supplierId: returnData.supplierId?._id || returnData.supplierId,
                notes: returnData.notes || '',
                items: returnData.items?.map(item => ({
                    productVariantId: item.productVariantId,
                    productName: item.productName || 'Unknown Product', // Backend might not store name
                    sku: item.sku || 'N/A',
                    quantity: item.quantity,
                    reason: item.reason
                })) || []
            });
        }
    }, [returnData, isEdit, reset]);

    const addItem = (variant) => {
        if (items.some(i => i.productVariantId === variant.productVariantId)) {
            toast.warning("Item already added");
            return;
        }
        append({
            productVariantId: variant.productVariantId,
            productName: `${variant.productName} (${variant.variantName})`,
            sku: variant.sku,
            quantity: 1,
            reason: RETURN_REASONS[0]
        });
    };

    const onSubmit = async (data) => {
        // Validate active supplier
        const selectedSupplier = suppliersData?.data?.find(s => s._id === data.supplierId);
        if (selectedSupplier && selectedSupplier.status === 'Inactive') {
            toast.error('Cannot save return for an inactive supplier');
            return;
        }

        try {
            if (isEdit) {
                await updateReturn({ id, ...data }).unwrap();
                toast.success("Return Updated");
            } else {
                await createReturn(data).unwrap();
                toast.success("Return Draft Created");
            }
            navigate('/inventory/returns-to-supplier');
        } catch (err) {
            toast.error(err?.data?.message || "Failed to save return");
        }
    };

    if (isEdit && isLoadingData) return <div className="p-5 text-center"><Spinner color="primary" /></div>;

    return (
        <Fragment>
            <Breadcrumbs mainTitle={isEdit ? "Edit Return" : "New Return"} parent="Inventory" title="Supplier Return Form" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>{isEdit ? "Edit Supplier Return" : "Create Supplier Return"}</h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit(onSubmit, (errs) => {
                                    console.error(errs);
                                    toast.error("Please fill all required fields");
                                })}>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Source Store</Label>
                                                <Controller
                                                    name="storeId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input type="select" {...field} invalid={!!errors.storeId}>
                                                            <option value="">-- Select Store --</option>
                                                            {storesData?.data?.map(w => (
                                                                <option key={w._id} value={w._id}>{w.storeName} {w.storeNumber ? `(${w.storeNumber})` : ''}</option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                />
                                                <div className="invalid-feedback">{errors.storeId?.message}</div>
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label>Supplier</Label>
                                                <Controller
                                                    name="supplierId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input type="select" {...field} invalid={!!errors.supplierId}>
                                                            <option value="">-- Select Supplier --</option>
                                                            {suppliersData?.data?.filter(s => s.status !== 'Inactive' || s._id === field.value).map(s => (
                                                                <option key={s._id} value={s._id}>
                                                                    {s.name} {s.status === 'Inactive' ? '(Inactive)' : ''}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                />
                                                <div className="invalid-feedback">{errors.supplierId?.message}</div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="12">
                                            <FormGroup>
                                                <Label>Notes</Label>
                                                <Input type="textarea" {...register('notes')} placeholder="Add any additional context..." />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <hr />
                                    <h6>Items</h6>

                                    <div className="mb-3">
                                        <ProductSearch
                                            onProductSelect={addItem}
                                            placeholder="Search products to add..."
                                            selectedIds={fields.map(f => f.productVariantId)}
                                            storeId={storeId}
                                        />
                                    </div>

                                    <Table responsive bordered hover>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Product</th>
                                                <th>SKU</th>
                                                <th style={{ width: '120px' }}>Qty</th>
                                                <th style={{ width: '200px' }}>Reason</th>
                                                <th style={{ width: '50px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fields.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>{item.productName}</td>
                                                    <td>{item.sku}</td>
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                                                            invalid={!!errors.items?.[index]?.quantity}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Input
                                                            type="select"
                                                            {...register(`items.${index}.reason`)}
                                                            invalid={!!errors.items?.[index]?.reason}
                                                        >
                                                            {RETURN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                                        </Input>
                                                    </td>
                                                    <td>
                                                        <Button color="danger" size="xs" onClick={() => remove(index)}><Trash size={14} /></Button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {fields.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted">No items added. Search above to add items.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                    {errors.items && <div className="text-danger mb-3">{errors.items.message}</div>}

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <Button color="light" onClick={() => navigate('/inventory/returns-to-supplier')}>Cancel</Button>
                                        <Button color="primary" type="submit" disabled={isCreating || isUpdating}>
                                            {(isCreating || isUpdating) ? <Spinner size="sm" /> : (isEdit ? 'Update Return' : 'Create Draft')}
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default ReturnToSupplierForm;
